import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Container, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'success', 'error', 'info'

  const handleSendOTP = async () => {
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post(`${API_URL}/auth/forgot-password/`, { email });
      setMessage(response.data.message || 'OTP sent! Check your Django console for the OTP.');
      setMessageType('success');
      setStep(2);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to send OTP. Please check your email.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setMessageType('error');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters');
      setMessageType('error');
      return;
    }
    
    try {
      setLoading(true);
      setMessage('');
      const response = await axios.post(`${API_URL}/auth/reset-password/`, {
        email,
        otp,
        new_password: newPassword
      });
      setMessage(response.data.message || 'Password reset successful! Redirecting to login...');
      setMessageType('success');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to reset password. Please check your OTP.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: '#f5f5f5'
    }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '50px', marginBottom: '16px' }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
              Forgot Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === 1 ? 'Enter your email to receive OTP' : 'Enter OTP and new password'}
            </Typography>
          </Box>

          {step === 1 ? (
            <>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="your@email.com"
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleSendOTP}
                disabled={loading || !email}
                sx={{ 
                  mb: 2, 
                  py: 1.5,
                  bgcolor: '#1976d2',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Check your Django console for the OTP (in development mode)
              </Alert>
              
              <TextField
                fullWidth
                label="OTP Code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Enter 6-digit OTP"
                inputProps={{ maxLength: 6 }}
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Enter new password"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="Confirm new password"
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading || !otp || !newPassword || !confirmPassword}
                sx={{ 
                  mb: 2, 
                  py: 1.5,
                  bgcolor: '#1976d2',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </>
          )}

          {message && (
            <Alert severity={messageType} sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none' }}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
